import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Switch, styled, CircularProgress } from '@mui/material';
import { tweetsAPI } from '../../utils/api';
import TweetCard from '../Tweets/TweetCard';

const Wrapper = styled(Box)({
  marginTop: '16px',
});

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '12px',
});

const HeaderText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const Kicker = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  color: '#86868B',
});

const Sub = styled(Typography)({
  fontSize: '13px',
  color: '#6E6E73',
});

const ToggleLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#1D1D1F',
  letterSpacing: '-0.01em',
});

const Rail = styled(Box)(({ disabled }) => ({
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  paddingBottom: '8px',
  scrollSnapType: 'x mandatory',
  opacity: disabled ? 0.35 : 1,
  filter: disabled ? 'grayscale(0.4)' : 'none',
  pointerEvents: disabled ? 'none' : 'auto',
  transition: 'opacity 0.25s ease, filter 0.25s ease',
  '&::-webkit-scrollbar': { height: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.12)',
    borderRadius: '3px',
  },
}));

const TweetSlot = styled(Box)({
  flex: '0 0 320px',
  scrollSnapAlign: 'start',
  maxWidth: '320px',
});

const Empty = styled(Typography)({
  fontSize: '13px',
  color: '#86868B',
  textAlign: 'center',
  padding: '24px 0',
});

const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#FFFFFF',
    '& + .MuiSwitch-track': {
      backgroundColor: '#000000',
      opacity: 1,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#D2D2D7',
    opacity: 1,
  },
});

/**
 * Simplified replacement for TweetFeedSection — single boolean toggle,
 * horizontal carousel of all tweets matching the trade's players.
 * Mirrors the recipient's view in TradeRatingView.
 */
const TradeBuzzCarousel = ({ players = [], includeTweets, onToggleInclude, onTweetsChange }) => {
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(false);

  const playerIds = useMemo(
    () => players.map((p) => String(p.id)).filter(Boolean),
    [players]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (playerIds.length === 0) {
        setTweets([]);
        onTweetsChange?.([]);
        return;
      }
      setLoading(true);
      try {
        const res = await tweetsAPI.forPlayers(playerIds, 150);
        if (cancelled) return;
        const fetched = res.data.tweets || [];
        setTweets(fetched);
        onTweetsChange?.(fetched.map((t) => t.id));
      } catch (err) {
        if (!cancelled) {
          setTweets([]);
          onTweetsChange?.([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerIds.join(',')]);

  return (
    <Wrapper>
      <HeaderRow>
        <HeaderText>
          <Kicker>The chatter</Kicker>
          <Sub>
            {tweets.length > 0
              ? `${tweets.length} tweet${tweets.length === 1 ? '' : 's'} about these players`
              : 'No buzz on these players yet'}
          </Sub>
        </HeaderText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ToggleLabel>{includeTweets ? 'Included' : 'Off'}</ToggleLabel>
          <StyledSwitch
            checked={!!includeTweets}
            onChange={onToggleInclude}
            disabled={tweets.length === 0}
          />
        </Box>
      </HeaderRow>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <CircularProgress size={20} sx={{ color: '#86868B' }} />
        </Box>
      ) : tweets.length === 0 ? (
        <Empty>Nothing in the timeline yet.</Empty>
      ) : (
        <Rail disabled={!includeTweets}>
          {tweets.map((t) => (
            <TweetSlot key={t.id}>
              <TweetCard tweet={t} showMetrics={false} />
            </TweetSlot>
          ))}
        </Rail>
      )}
    </Wrapper>
  );
};

export default TradeBuzzCarousel;
